import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Driver } from '../driver/driver.model';
import { Vehicle } from '../vehicle/vehicle.model';
import { VehicleCapacity } from '../vehicle/params/vehicle-capacity';
import { VehicleConfig } from '../vehicle/params/vehicle-config';
import { VehicleMake } from '../vehicle/params/vehicle-make';
import { VehicleModel } from '../vehicle/params/vehicle-model';
import { VehicleType } from '../vehicle/params/vehicle-type';
import { VehicleYear } from '../vehicle/params/vehicle-year';
import { Country } from '../../models/common/country.model';
import { City } from '../../models/common/city.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { CrmService, TransporterFleetOption } from '../../crm/crm.service';
import { RentalAgreement } from '../rental/rental.model';
import { RentalService } from '../rental/rental.service';
import { QuickFleetOnboardingService } from './quick-fleet-onboarding.service';

export type FleetResourceSource = 'owned' | 'freelance' | 'rental' | 'transporter';

export interface QuickFleetOnboardingResult {
    driver: Driver;
    vehicle: Vehicle;
    source: FleetResourceSource;
    transporterCrmClientId?: number | null;
}

interface FieldValue {
    value?: string | null;
    confidence?: number;
}

interface DriverExtractionResult {
    legalName?: string;
    residenceNumber?: string;
    licenseNumber?: string;
    dateOfBirth?: string;
    residenceExpiryDate?: string;
    licenseExpiryDate?: string;
    nationality?: string;
    address?: string;
}

interface VehicleExtractionResult {
    plateNumber?: string;
    registrationNumber?: string;
    engineNumber?: string;
    registrationExpiry?: string;
    make?: string;
    model?: string;
    year?: string;
    vehicleType?: string;
    vehicleConfig?: string;
    vehicleCapacity?: string;
}

interface DriverFieldsConfidence {
    legalName?: FieldValue;
    residenceNumber?: FieldValue;
    licenseNumber?: FieldValue;
    dateOfBirth?: FieldValue;
    residenceExpiryDate?: FieldValue;
    licenseExpiryDate?: FieldValue;
    nationality?: FieldValue;
    address?: FieldValue;
}

interface VehicleFieldsConfidence {
    plateNumber?: FieldValue;
    registrationNumber?: FieldValue;
    engineNumber?: FieldValue;
    registrationExpiry?: FieldValue;
    make?: FieldValue;
    model?: FieldValue;
    year?: FieldValue;
    vehicleType?: FieldValue;
    vehicleConfig?: FieldValue;
    vehicleCapacity?: FieldValue;
}

interface OnboardingExtractionResult {
    driver?: DriverFieldsConfidence | null;
    vehicle?: VehicleFieldsConfidence | null;
}

type DocumentUploadMode = 'separate' | 'combined';

@Component({
    selector: 'app-quick-fleet-onboarding-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        DialogModule,
        ButtonModule,
        FileUploadModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        InputNumberModule,
        DatePickerModule,
        SelectModule,
        SelectButtonModule,
        TagModule,
        TextareaModule,
        ProgressSpinnerModule
    ],
    templateUrl: './quick-fleet-onboarding-dialog.component.html',
    styleUrls: ['./quick-fleet-onboarding-dialog.component.css']
})
export class QuickFleetOnboardingDialogComponent implements OnChanges {
    @Input() visible = false;
    @Input() defaultSource: FleetResourceSource = 'owned';
    @Input() defaultTransporterCrmClientId: number | null = null;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() completed = new EventEmitter<QuickFleetOnboardingResult>();

    readonly sourceOptions = [
        { label: 'Owned fleet', value: 'owned', icon: 'pi pi-building' },
        { label: 'Freelance', value: 'freelance', icon: 'pi pi-user' },
        { label: 'Rental', value: 'rental', icon: 'pi pi-key' },
        { label: 'Transporter', value: 'transporter', icon: 'pi pi-building' }
    ];
    readonly uploadModeOptions = [
        { label: '3 separate images', value: 'separate', icon: 'pi pi-images' },
        { label: '1 combined image', value: 'combined', icon: 'pi pi-image' }
    ];
    form: FormGroup;
    source: FleetResourceSource = 'owned';
    documentMode: DocumentUploadMode = 'separate';
    combinedFile?: File;
    residenceFile?: File;
    licenseFile?: File;
    registrationFile?: File;
    extracting = false;
    saving = false;
    errorMessage = '';
    extracted = false;

    /** Threshold below which an AI-extracted field is flagged as low-confidence in the UI. */
    readonly lowConfidenceThreshold = 0.75;

    /** Per-field confidence map keyed by form-control name. Used to mark low-confidence inputs. */
    fieldConfidence: Record<string, number | undefined> = {};

    /** Names of form controls that the AI populated with low confidence on the last extract. */
    lowConfidenceFields = new Set<string>();

    countries: Country[] = [];
    residingCities: City[] = [];
    vehicleTypes: VehicleType[] = [];
    vehicleMakes: VehicleMake[] = [];
    transporterOptions: TransporterFleetOption[] = [];
    transporterOptionsLoading = false;
    rentalAgreements: RentalAgreement[] = [];
    rentalAgreementOptions: { label: string; value: number }[] = [];
    rentalAgreementsLoading = false;

    constructor(
        fb: FormBuilder,
        private http: HttpClient,
        private session: SessionStorageService,
        private baseMasterData: BaseMasterDataService,
        private crmService: CrmService,
        private rentalService: RentalService,
        private quickFleetOnboarding: QuickFleetOnboardingService,
        private router: Router
    ) {
        this.form = fb.group({
            name: ['', Validators.required],
            phoneNumber: ['', Validators.required],
            residenceNumber: ['', Validators.required],
            residenceExpiryDate: [null],
            licenseNumber: ['', Validators.required],
            licenseExpiryDate: [null],
            nationality: [null],
            residingCountry: [null, Validators.required],
            residingCity: [null, Validators.required],
            plateNumber: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9\s]+$/)]],
            registrationNumber: ['', Validators.required],
            engineNumber: [''],
            chassisNumber: [''],
            registrationExpiry: [null],
            vehicleType: [null, Validators.required],
            vehicleConfig: [null, Validators.required],
            vehicleCapacity: [null, Validators.required],
            vehicleMake: [null],
            vehicleModel: [null],
            vehicleYear: [null],
            transporterCrmClientId: [null],
            rentalAgreementId: [null]
        });
        this.form.get('plateNumber')?.valueChanges.subscribe(value => {
            const normalized = typeof value === 'string'
                ? value.replace(/\s+/g, '').toUpperCase()
                : value;
            if (normalized !== value) {
                this.form.get('plateNumber')?.setValue(normalized, { emitEvent: false });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible']?.currentValue === true) {
            this.reset();
        }
    }

    get hasAllDocuments(): boolean {
        return this.documentMode === 'combined'
            ? !!this.combinedFile
            : !!this.residenceFile && !!this.licenseFile && !!this.registrationFile;
    }

    get isCombinedMode(): boolean {
        return this.documentMode === 'combined';
    }

    get vehicleConfigs(): VehicleConfig[] {
        return this.form.get('vehicleType')?.value?.vehicleConfigs ?? [];
    }

    get vehicleCapacities(): VehicleCapacity[] {
        return this.form.get('vehicleType')?.value?.vehicleCapacities ?? [];
    }

    get vehicleModels(): VehicleModel[] {
        return this.form.get('vehicleMake')?.value?.models ?? [];
    }

    get vehicleYears(): VehicleYear[] {
        return this.form.get('vehicleModel')?.value?.years ?? [];
    }

    get isRental(): boolean {
        return this.source === 'rental';
    }

    get isTransporter(): boolean {
        return this.source === 'transporter';
    }

    onSourceChange(source: FleetResourceSource): void {
        this.source = source;
        const transporterControl = this.form.get('transporterCrmClientId');
        const rentalAgreementControl = this.form.get('rentalAgreementId');
        transporterControl?.setValidators(this.isTransporter ? [Validators.required] : []);
        transporterControl?.updateValueAndValidity();
        rentalAgreementControl?.setValidators(this.isRental ? [Validators.required] : []);
        rentalAgreementControl?.updateValueAndValidity();
        if (this.isTransporter) {
            this.loadTransporters();
        } else {
            transporterControl?.setValue(null);
        }
        if (this.isRental) {
            this.loadRentalAgreements();
        } else {
            rentalAgreementControl?.setValue(null);
        }
    }

    selectDocument(kind: 'residence' | 'license' | 'registration' | 'combined', event: any): void {
        const file = event?.currentFiles?.[0] ?? event?.files?.[0];
        if (!file) return;
        if (kind === 'residence') this.residenceFile = file;
        if (kind === 'license') this.licenseFile = file;
        if (kind === 'registration') this.registrationFile = file;
        if (kind === 'combined') this.combinedFile = file;
        this.extracted = false;
    }

    clearDocument(kind: 'residence' | 'license' | 'registration' | 'combined'): void {
        if (kind === 'residence') this.residenceFile = undefined;
        if (kind === 'license') this.licenseFile = undefined;
        if (kind === 'registration') this.registrationFile = undefined;
        if (kind === 'combined') this.combinedFile = undefined;
        this.extracted = false;
    }

    setDocumentMode(mode: DocumentUploadMode): void {
        if (this.documentMode === mode) return;
        this.documentMode = mode;
        this.residenceFile = undefined;
        this.licenseFile = undefined;
        this.registrationFile = undefined;
        this.combinedFile = undefined;
        this.extracted = false;
        this.lowConfidenceFields.clear();
        this.fieldConfidence = {};
    }

    extract(): void {
        if (!this.hasAllDocuments || this.extracting) return;
        this.errorMessage = '';
        this.extracting = true;
        this.lowConfidenceFields.clear();
        const request$ = this.isCombinedMode
            ? this.extractCombinedDocument(this.combinedFile!).pipe(map(result => ({
                residence: result?.driver ?? {},
                license: result?.driver ?? {},
                vehicle: result?.vehicle ?? {}
            })))
            : forkJoin({
                residence: this.extractDriverDocument(this.residenceFile!, 'residence'),
                license: this.extractDriverDocument(this.licenseFile!, 'license'),
                vehicle: this.extractVehicleDocument(this.registrationFile!)
            });
        request$.pipe(finalize(() => this.extracting = false)).subscribe({
            next: data => {
                this.applyExtraction(data.residence, data.license, data.vehicle);
                this.extracted = true;
            },
            error: error => this.errorMessage = this.errorDetail(error, this.isCombinedMode
                ? 'AI could not read the combined document. Please review the image and try again.'
                : 'AI could not read one or more documents. Please review the images and try again.')
        });
    }

    save(): void {
        if (this.saving) return;
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.errorMessage = 'Complete the highlighted driver, vehicle, and fleet-source fields before linking the pair.';
            return;
        }
        const company = this.session.getCompany();
        if (!company?.id || (!this.isRental && !this.isTransporter && !company.transportationAgencyId)) {
            this.errorMessage = 'Your company needs a transportation agency before a driver and vehicle can be linked.';
            return;
        }
        this.saving = true;
        this.errorMessage = '';
        const owningAgencyId = this.isRental || this.isTransporter ? null : (company.transportationAgencyId ?? null);
        const driver = this.buildDriver(owningAgencyId);
        const vehicle = this.buildVehicle(owningAgencyId);

        const onboarding = this.quickFleetOnboarding.onboardPair({
            owningCompanyId: company.id,
            source: this.source,
            transporterCrmClientId: this.form.get('transporterCrmClientId')?.value ?? null,
            rentalAgreementId: this.form.get('rentalAgreementId')?.value ?? null,
            driver,
            vehicle
        });

        onboarding.pipe(
            finalize(() => this.saving = false)
        ).subscribe({
            next: response => {
                if (!response.success || !response.data?.driver || !response.data?.vehicle) {
                    this.errorMessage = response.message || 'The driver and vehicle could not be linked. Correct the details and retry.';
                    return;
                }
                this.completed.emit({
                    driver: response.data.driver,
                    vehicle: response.data.vehicle,
                    source: this.source,
                    transporterCrmClientId: this.form.get('transporterCrmClientId')?.value ?? null
                });
                this.close();
            },
            error: error => this.errorMessage = this.errorDetail(error,
                'The driver and vehicle were not linked. Correct the details and retry; no partial fleet records were saved.')
        });
    }

    onVehicleTypeChange(): void {
        const type = this.form.get('vehicleType')?.value as VehicleType | null;
        this.form.patchValue({ vehicleConfig: type?.vehicleConfigs?.[0] ?? null, vehicleCapacity: type?.vehicleCapacities?.[0] ?? null });
    }

    onResidingCountryChange(): void {
        const country = this.form.get('residingCountry')?.value as Country | null;
        this.loadResidingCities(country?.id);
    }

    onVehicleMakeChange(): void {
        const make = this.form.get('vehicleMake')?.value as VehicleMake | null;
        const model = make?.models?.[0] ?? null;
        this.form.patchValue({ vehicleModel: model, vehicleYear: model?.years?.[0] ?? null });
    }

    onVehicleModelChange(): void {
        const model = this.form.get('vehicleModel')?.value as VehicleModel | null;
        this.form.patchValue({ vehicleYear: model?.years?.[0] ?? null });
    }

    close(): void {
        this.visibleChange.emit(false);
    }

    createRentalAgreement(): void {
        this.close();
        this.router.navigate(['/fleet/rentals/agreements/new']);
    }

    rentalAgreementLabel(agreement: RentalAgreement): string {
        const reference = agreement.agreementNumber || `Agreement #${agreement.rentalAgreementId}`;
        const supplier = agreement.supplierName?.trim();
        return supplier ? `${reference} — ${supplier}` : reference;
    }

    private reset(): void {
        this.source = this.defaultSource;
        this.documentMode = 'separate';
        this.combinedFile = undefined;
        this.residenceFile = undefined;
        this.licenseFile = undefined;
        this.registrationFile = undefined;
        this.extracting = false;
        this.saving = false;
        this.extracted = false;
        this.errorMessage = '';
        this.lowConfidenceFields.clear();
        this.fieldConfidence = {};
        this.countries = this.session.getCountryList() ?? [];
        this.residingCities = [];
        this.vehicleTypes = this.session.getVehicleTypes() ?? [];
        this.vehicleMakes = this.session.getVehicleMakes() ?? [];
        const country = this.countries.find(item => (item.name || '').toLowerCase().includes('saudi')) ?? this.countries[0] ?? null;
        const type = this.vehicleTypes[0] ?? null;
        this.form.reset({
            residingCountry: country,
            nationality: country,
            vehicleType: type,
            vehicleConfig: type?.vehicleConfigs?.[0] ?? null,
            vehicleCapacity: type?.vehicleCapacities?.[0] ?? null,
            transporterCrmClientId: this.defaultTransporterCrmClientId
        });
        this.loadResidingCities(country?.id);
        this.onSourceChange(this.source);
    }

    private loadResidingCities(countryId?: number): void {
        this.residingCities = [];
        this.form.get('residingCity')?.setValue(null, { emitEvent: false });
        if (!countryId) return;
        this.baseMasterData.getCities('en-US', countryId).pipe(
            catchError(() => of([] as City[]))
        ).subscribe(cities => this.residingCities = cities ?? []);
    }

    private extractDriverDocument(file: File, type: 'residence' | 'license') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        return this.http.post<DriverFieldsConfidence>(`${environment.aiServiceApiUrl}driver/extract-info-vision`, formData);
    }

    private extractVehicleDocument(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<VehicleFieldsConfidence>(`${environment.aiServiceApiUrl}vehicle/extract-info-vision`, formData);
    }

    private extractCombinedDocument(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<OnboardingExtractionResult>(`${environment.aiServiceApiUrl}onboarding/extract-info`, formData);
    }

    /**
     * Flatten a confidence-wrapped object into the existing flat shape used by
     * {@link applyExtraction}, so the downstream patch logic stays unchanged.
     */
    private flattenDriver(src?: DriverFieldsConfidence | null): DriverExtractionResult {
        if (!src) return {};
        return {
            legalName: src.legalName?.value ?? undefined,
            residenceNumber: src.residenceNumber?.value ?? undefined,
            licenseNumber: src.licenseNumber?.value ?? undefined,
            dateOfBirth: src.dateOfBirth?.value ?? undefined,
            residenceExpiryDate: src.residenceExpiryDate?.value ?? undefined,
            licenseExpiryDate: src.licenseExpiryDate?.value ?? undefined,
            nationality: src.nationality?.value ?? undefined,
            address: src.address?.value ?? undefined
        };
    }

    private flattenVehicle(src?: VehicleFieldsConfidence | null): VehicleExtractionResult {
        if (!src) return {};
        return {
            plateNumber: src.plateNumber?.value ?? undefined,
            registrationNumber: src.registrationNumber?.value ?? undefined,
            engineNumber: src.engineNumber?.value ?? undefined,
            registrationExpiry: src.registrationExpiry?.value ?? undefined,
            make: src.make?.value ?? undefined,
            model: src.model?.value ?? undefined,
            year: src.year?.value ?? undefined,
            vehicleType: src.vehicleType?.value ?? undefined,
            vehicleConfig: src.vehicleConfig?.value ?? undefined,
            vehicleCapacity: src.vehicleCapacity?.value ?? undefined
        };
    }

    private applyExtraction(
        residence: DriverFieldsConfidence | DriverExtractionResult,
        license: DriverFieldsConfidence | DriverExtractionResult,
        vehicle: VehicleFieldsConfidence | VehicleExtractionResult): void {

        // Normalize to confidence-aware shapes (legacy flat strings are treated as 0 confidence).
        const r = this.normalizeDriverShape(residence);
        const l = this.normalizeDriverShape(license);
        const v = this.normalizeVehicleShape(vehicle);

        const make = this.findByName(this.vehicleMakes, v?.make?.value ?? undefined);
        const model = this.findByName(make?.models ?? [], v?.model?.value ?? undefined);
        const type = this.findByName(this.vehicleTypes, v?.vehicleType?.value ?? undefined) ?? this.form.get('vehicleType')?.value;
        const config = this.findByName(type?.vehicleConfigs ?? [], v?.vehicleConfig?.value ?? undefined) ?? type?.vehicleConfigs?.[0] ?? null;
        const capacity = this.findByName(type?.vehicleCapacities ?? [], v?.vehicleCapacity?.value ?? undefined) ?? type?.vehicleCapacities?.[0] ?? null;
        const year = (model?.years ?? []).find(item => String(item.year) === String(v?.year?.value)) ?? model?.years?.[0] ?? null;

        // Capture confidence for each field the AI populated so the UI can flag low values.
        this.recordConfidence('name', r?.legalName, l?.legalName);
        this.recordConfidence('residenceNumber', r?.residenceNumber);
        this.recordConfidence('residenceExpiryDate', r?.residenceExpiryDate);
        this.recordConfidence('licenseNumber', l?.licenseNumber);
        this.recordConfidence('licenseExpiryDate', l?.licenseExpiryDate);
        this.recordConfidence('plateNumber', v?.plateNumber);
        this.recordConfidence('registrationNumber', v?.registrationNumber);
        this.recordConfidence('engineNumber', v?.engineNumber);
        this.recordConfidence('registrationExpiry', v?.registrationExpiry);

        this.form.patchValue({
            name: this.firstString(r?.legalName?.value, l?.legalName?.value) || this.form.get('name')?.value || '',
            residenceNumber: r?.residenceNumber?.value || this.form.get('residenceNumber')?.value || '',
            residenceExpiryDate: this.toDate(r?.residenceExpiryDate?.value),
            licenseNumber: l?.licenseNumber?.value || this.form.get('licenseNumber')?.value || '',
            licenseExpiryDate: this.toDate(l?.licenseExpiryDate?.value),
            plateNumber: v?.plateNumber?.value || this.form.get('plateNumber')?.value || '',
            registrationNumber: v?.registrationNumber?.value || this.form.get('registrationNumber')?.value || '',
            engineNumber: v?.engineNumber?.value || this.form.get('engineNumber')?.value || '',
            registrationExpiry: this.toDate(v?.registrationExpiry?.value),
            vehicleMake: make ?? null,
            vehicleModel: model ?? null,
            vehicleYear: year,
            vehicleType: type,
            vehicleConfig: config,
            vehicleCapacity: capacity
        });
    }

    private normalizeDriverShape(input: DriverFieldsConfidence | DriverExtractionResult | null | undefined): DriverFieldsConfidence | null {
        if (!input) return null;
        if ('legalName' in input && (input as DriverFieldsConfidence).legalName && typeof (input as DriverFieldsConfidence).legalName === 'object'
            && 'value' in ((input as DriverFieldsConfidence).legalName as FieldValue)) {
            return input as DriverFieldsConfidence;
        }
        const flat = input as DriverExtractionResult;
        return {
            legalName: { value: flat.legalName, confidence: 0 },
            residenceNumber: { value: flat.residenceNumber, confidence: 0 },
            licenseNumber: { value: flat.licenseNumber, confidence: 0 },
            dateOfBirth: { value: flat.dateOfBirth, confidence: 0 },
            residenceExpiryDate: { value: flat.residenceExpiryDate, confidence: 0 },
            licenseExpiryDate: { value: flat.licenseExpiryDate, confidence: 0 },
            nationality: { value: flat.nationality, confidence: 0 },
            address: { value: flat.address, confidence: 0 }
        };
    }

    private normalizeVehicleShape(input: VehicleFieldsConfidence | VehicleExtractionResult | null | undefined): VehicleFieldsConfidence | null {
        if (!input) return null;
        if ('plateNumber' in input && (input as VehicleFieldsConfidence).plateNumber && typeof (input as VehicleFieldsConfidence).plateNumber === 'object'
            && 'value' in ((input as VehicleFieldsConfidence).plateNumber as FieldValue)) {
            return input as VehicleFieldsConfidence;
        }
        const flat = input as VehicleExtractionResult;
        return {
            plateNumber: { value: flat.plateNumber, confidence: 0 },
            registrationNumber: { value: flat.registrationNumber, confidence: 0 },
            engineNumber: { value: flat.engineNumber, confidence: 0 },
            registrationExpiry: { value: flat.registrationExpiry, confidence: 0 },
            make: { value: flat.make, confidence: 0 },
            model: { value: flat.model, confidence: 0 },
            year: { value: flat.year, confidence: 0 },
            vehicleType: { value: flat.vehicleType, confidence: 0 },
            vehicleConfig: { value: flat.vehicleConfig, confidence: 0 },
            vehicleCapacity: { value: flat.vehicleCapacity, confidence: 0 }
        };
    }

    private recordConfidence(formControlName: string, ...candidates: (FieldValue | undefined | null)[]): void {
        const best = candidates.find(c => c && (c.value !== undefined && c.value !== null && c.value !== ''));
        if (!best) {
            this.fieldConfidence[formControlName] = undefined;
            return;
        }
        this.fieldConfidence[formControlName] = best.confidence ?? 0;
        if ((best.confidence ?? 0) < this.lowConfidenceThreshold) {
            this.lowConfidenceFields.add(formControlName);
        }
    }

    isLowConfidence(formControlName: string): boolean {
        return this.lowConfidenceFields.has(formControlName);
    }

    private firstString(...values: (string | null | undefined)[]): string {
        for (const v of values) {
            if (v !== undefined && v !== null && v !== '') return v;
        }
        return '';
    }

    private buildDriver(transportationAgencyId: number | null): Driver {
        const value = this.form.getRawValue();
        const driver = new Driver();
        driver.name = value.name;
        driver.legalName = value.name;
        driver.phoneNumber = value.phoneNumber;
        driver.residenceNumber = value.residenceNumber;
        driver.residenceExpiryDate = this.toTimestamp(value.residenceExpiryDate);
        driver.licenseNumber = value.licenseNumber;
        driver.licenseExpiryDate = this.toTimestamp(value.licenseExpiryDate);
        driver.nationality = value.nationality;
        driver.residingCountry = value.residingCountry;
        driver.residingCity = value.residingCity;
        driver.preferredAddress = value.residingCity?.cityName ?? undefined;
        driver.preferredLocation = value.residingCity?.coordinates ?? undefined;
        driver.identityType = 'IQAMA';
        if (transportationAgencyId) {
            driver.currentTransportationAgency = { rowId: transportationAgencyId } as any;
        }
        driver.transporterCrmClientId = value.transporterCrmClientId ?? null;
        return driver;
    }

    private buildVehicle(transportationAgencyId: number | null): Vehicle {
        const value = this.form.getRawValue();
        const vehicle = new Vehicle();
        vehicle.plateNumber = value.plateNumber;
        vehicle.registrationNumber = value.registrationNumber;
        vehicle.engineNumber = value.engineNumber;
        vehicle.chassisNumber = value.chassisNumber;
        vehicle.registrationExpiry = this.toTimestamp(value.registrationExpiry);
        vehicle.type = value.vehicleType;
        vehicle.config = value.vehicleConfig;
        vehicle.capacity = value.vehicleCapacity;
        vehicle.make = value.vehicleMake;
        vehicle.model = value.vehicleModel;
        vehicle.year = value.vehicleYear;
        vehicle.registrationCountry = value.residingCountry;
        // A guided pair represents one locally based driver and vehicle. Full vehicle edit can
        // later refine this if the registration city differs from the driver's residing city.
        vehicle.registrationCity = value.residingCity;
        if (transportationAgencyId) {
            vehicle.transportationAgencyId = transportationAgencyId;
        }
        vehicle.transporterCrmClientId = value.transporterCrmClientId ?? null;
        vehicle.isActive = true;
        return vehicle;
    }

    private loadRentalAgreements(): void {
        const companyId = this.session.getCompany()?.id;
        if (!companyId) {
            this.rentalAgreements = [];
            this.rentalAgreementOptions = [];
            return;
        }
        this.rentalAgreementsLoading = true;
        this.rentalService.searchAgreements(companyId).pipe(
            finalize(() => this.rentalAgreementsLoading = false),
            catchError(() => {
                this.errorMessage = 'Unable to load rental agreements. Create one first or retry.';
                return of({ items: [] as RentalAgreement[] });
            })
        ).subscribe(response => {
            this.rentalAgreements = (response.items || []).filter(agreement => ['DRAFT', 'ACTIVE', 'SUSPENDED'].includes(agreement.status || ''));
            this.rentalAgreementOptions = this.rentalAgreements
                .filter((agreement): agreement is RentalAgreement & { rentalAgreementId: number } => agreement.rentalAgreementId != null)
                .map(agreement => ({ label: this.rentalAgreementLabel(agreement), value: agreement.rentalAgreementId }));
            const selected = this.form.get('rentalAgreementId')?.value;
            if (selected && !this.rentalAgreements.some(agreement => agreement.rentalAgreementId === selected)) {
                this.form.get('rentalAgreementId')?.setValue(null);
            }
        });
    }

    private loadTransporters(): void {
        const companyId = this.session.getCompany()?.id;
        if (!companyId) {
            this.transporterOptions = [];
            return;
        }
        this.transporterOptionsLoading = true;
        this.crmService.getScopedTransporters(companyId).pipe(
            finalize(() => this.transporterOptionsLoading = false),
            catchError(() => {
                this.errorMessage = 'Unable to load accessible transporters. Please retry or choose another fleet source.';
                return of([] as TransporterFleetOption[]);
            })
        ).subscribe(options => {
            this.transporterOptions = options ?? [];
            const selectedId = this.form.get('transporterCrmClientId')?.value;
            if (selectedId && !this.transporterOptions.some(option => option.crmClientId === selectedId)) {
                this.form.get('transporterCrmClientId')?.setValue(null);
            }
        });
    }

    private findByName<T extends { name?: string }>(items: T[], name?: string): T | undefined {
        if (!name?.trim()) return undefined;
        const normalized = name.trim().toLowerCase();
        return items.find(item => item.name?.trim().toLowerCase() === normalized)
            ?? items.find(item => item.name?.trim().toLowerCase().includes(normalized) || normalized.includes(item.name?.trim().toLowerCase() ?? ''));
    }

    private toDate(value?: string | null): Date | null {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private toTimestamp(value?: Date): number | undefined {
        return value instanceof Date && !Number.isNaN(value.getTime()) ? value.getTime() : undefined;
    }

    private errorDetail(error: any, fallback: string): string {
        const detail = error?.error?.message || error?.message;
        if (error?.status === 0 || /failed to fetch|network\s*error/i.test(String(detail ?? ''))) {
            return 'The AI document service could not be reached. Check your connection and try again. If this continues, contact support with the time of the attempt.';
        }
        return typeof detail === 'string' && detail.trim() ? detail : fallback;
    }
}
